import { DefaultError, UseMutationResult } from "@tanstack/react-query";
import { createContext, ReactNode, use, useCallback, useMemo, useState } from "react";
import { CalcType } from "../../enums";
import { CalcResult, CalcResultData } from "../types";
import { isApplicableResult, sortAndGroupCalcResults } from "../utils";

export type CalcResultsContextValue<TCalc, TResultData extends CalcResultData, U extends CalcType = CalcType> = {
  /** Raw server response */
  calcResults: CalcResult<TResultData, U>[];
  /** Results without the non-applicable ones */
  applicableResults: CalcResult<TResultData, U>[];
  /** Results grouped by institution */
  calcResultsGroupedByInstitution: CalcResult<TResultData, U>[][];
  isCalculating: boolean;
  calculate: (
    calcData: TCalc,
    options?: {
      /** Keeps the current results until the new ones arrive, so a failed request does not lose them */
      keepResults?: boolean;
      onSuccess?: (results: CalcResult<TResultData, U>[]) => void;
    },
  ) => void;
  clearResults: () => void;
};

/**
 * Creates a calculation results context for a single calculator type. The React Query mutation runs
 * inside the provider, so the calling component only deals with what happens after a successful calculation.
 */
export const createCalcResultsContext = <TCalc, TResultData extends CalcResultData, U extends CalcType = CalcType>(
  useCalculateMutation: () => UseMutationResult<CalcResult<TResultData, U>[], DefaultError, TCalc>,
) => {
  const CalcResultsContext = createContext<CalcResultsContextValue<TCalc, TResultData, U> | undefined>(undefined);

  const Provider = ({ children }: { children: ReactNode }) => {
    const [results, setResults] = useState<CalcResult<TResultData, U>[]>([]);
    const { mutate, isPending } = useCalculateMutation();

    const calculate = useCallback<CalcResultsContextValue<TCalc, TResultData, U>["calculate"]>(
      (calcData, options) => {
        if (!options?.keepResults) {
          setResults([]);
        }

        mutate(calcData, {
          onSuccess: (data) => {
            setResults(data);
            options?.onSuccess?.(data);
          },
        });
      },
      [mutate],
    );

    const clearResults = useCallback(() => setResults([]), []);

    const applicableResults = useMemo(() => results.filter(isApplicableResult), [results]);
    const calcResultsGroupedByInstitution = useMemo(() => sortAndGroupCalcResults(results), [results]);

    const value = useMemo<CalcResultsContextValue<TCalc, TResultData, U>>(
      () => ({
        calcResults: results,
        applicableResults,
        calcResultsGroupedByInstitution,
        isCalculating: isPending,
        calculate,
        clearResults,
      }),
      [results, applicableResults, calcResultsGroupedByInstitution, isPending, calculate, clearResults],
    );

    return <CalcResultsContext value={value}>{children}</CalcResultsContext>;
  };

  const useCalcResults = () => {
    const context = use(CalcResultsContext);

    if (!context) {
      throw new Error("useCalcResults must be used within its calc results provider");
    }

    return context;
  };

  return { Provider, useCalcResults };
};
